import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Building2,
  Bus,
  ClipboardList,
  Users,
  AlertTriangle,
  Settings,
  ChevronLeft,
  ChevronRight,
  UsersRound,
  Route,
  History,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Logo } from '@/components/common/Logo'
import { useUIStore } from '@/stores/ui.store'
import { usePermissions } from '@/hooks/usePermissions'
import type { Usuario } from '@/types/auth'
import type { Permission } from '@/lib/permissions'

interface NavItem {
  labelKey: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  permission?: Permission
}

const NAV_ITEMS: NavItem[] = [
  {
    labelKey: 'nav.dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    labelKey: 'nav.conteos',
    href: '/conteos',
    icon: UsersRound,
    permission: 'buses.read',
  },
  {
    labelKey: 'nav.clientes',
    href: '/clientes',
    icon: Building2,
    permission: 'clientes.read',
  },
  {
    labelKey: 'nav.buses',
    href: '/buses',
    icon: Bus,
    permission: 'buses.read',
  },
  {
    labelKey: 'nav.rutas',
    href: '/rutas',
    icon: Route,
    permission: 'buses.read',
  },
  {
    labelKey: 'nav.despachos',
    href: '/despachos',
    icon: ClipboardList,
    permission: 'buses.read',
  },
  {
    labelKey: 'nav.novedades',
    href: '/novedades',
    icon: AlertTriangle,
    permission: 'eventos.read',
  },
  {
    labelKey: 'nav.usuarios',
    href: '/usuarios',
    icon: Users,
    permission: 'usuarios.read',
  },
  {
    labelKey: 'nav.logActividad',
    href: '/log-actividad',
    icon: History,
    permission: 'eventos.read',
  },
]

interface SidebarProps {
  usuario: Usuario | null
}

export function Sidebar({ usuario }: SidebarProps) {
  const location = useLocation()
  const { t } = useTranslation()
  const { sidebarCollapsed, toggleSidebar } = useUIStore()
  const { can } = usePermissions({ usuario })

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (!item.permission) return true
    return can(item.permission)
  })

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen border-r bg-card transition-all duration-300',
        sidebarCollapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div
          className={cn(
            'flex h-16 items-center border-b px-4',
            sidebarCollapsed ? 'justify-center' : 'justify-between'
          )}
        >
          {!sidebarCollapsed && <Logo className="h-8" />}
          <Button variant="ghost" size="icon" onClick={toggleSidebar} className="h-8 w-8">
            {sidebarCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-2">
          {visibleItems.map((item) => {
            const isActive = location.pathname.startsWith(item.href)
            const Icon = item.icon
            const label = t(item.labelKey)

            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  sidebarCollapsed && 'justify-center px-2'
                )}
                title={sidebarCollapsed ? label : undefined}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>{label}</span>}
              </Link>
            )
          })}
        </nav>

        {/* Settings (bottom) */}
        <div className="border-t p-2">
          <Link
            to="/configuracion"
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
              sidebarCollapsed && 'justify-center px-2'
            )}
            title={sidebarCollapsed ? t('nav.configuracion') : undefined}
          >
            <Settings className="h-5 w-5 flex-shrink-0" />
            {!sidebarCollapsed && <span>{t('nav.configuracion')}</span>}
          </Link>
        </div>
      </div>
    </aside>
  )
}
